import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { TokenExpired } from '../actions/auth-actions';
import { environment } from 'src/environments/environment';
import { ChatTokenService } from 'src/app/pages/chat/services/chat-token.service';
import { GoogleAnalyticsService } from 'src/modules/analytics/services/google-analytics.service';
import { AnalyticsEventName } from 'src/modules/analytics/models/analytics-event-name.enum';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly chatTokenService = inject(ChatTokenService);
  private readonly analyticsService = inject(GoogleAnalyticsService);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const chatApiUrl = environment.chatApiUrl;

    if (chatApiUrl && request.url.startsWith(chatApiUrl)) {
      // ensureToken() refreshes if the cached token is missing or expired, so a stale token is never sent.
      // catchError only guards the token fetch — real HTTP errors from next.handle() still propagate to callers.
      return this.chatTokenService.ensureToken().pipe(
        catchError(() => of(null)),
        switchMap(chatToken => {
          const authedRequest = chatToken
            ? request.clone({ headers: request.headers.set('Authorization', `Bearer ${chatToken}`) })
            : request;
          return next.handle(authedRequest);
        })
      );
    }

    const responseType = request.responseType;

    if (!this.authService.isAuthenticated()) {
      return next.handle(request);
    }

    const apiToken = this.authService.getToken();

    if (!apiToken) {
      return next.handle(request);
    }

    const requestParameters = {
      headers: request.headers.set('Authorization', apiToken),
      responseType
    };

    return next.handle(request.clone(requestParameters)).pipe(
      tap({
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status == 401) {
              this.analyticsService.trackEvent(AnalyticsEventName.SessionAutoLogout);
              this.store.dispatch(new TokenExpired());
            }
          }
        }
      })
    );
  }
}
